/**
 * The 14 High Level Learning Outcomes of the FICM ICM Curriculum 2021 (v1.2).
 *
 * Kept as plain data with no logic embedded, because the 2021 curriculum will
 * be revised and a future version should be a data edit plus a migration
 * mapping old numbers to new. Do not scatter HiLLO numbers as literals through
 * the codebase — import them.
 *
 * `targetLevel` is the capability level expected at CCT. It appears alongside
 * each HiLLO in the PDF for context only. **Never render a pass/fail, a RAG
 * rating, or a progress bar against it.** The app reports activity; the
 * Educational Supervisor judges. See CURRICULUM.md.
 */
export const HILLOS = [
    {
        number: 1,
        shortLabel: 'Organisational & legal',
        description: 'Function successfully within NHS organisational and management systems whilst adhering to the appropriate legal and ethical framework',
        targetLevel: 4,
        category: 'generic',
    },
    {
        number: 2,
        shortLabel: 'Patient safety & QI',
        description: 'Focus on patient safety and deliver effective quality improvement, whilst practising within established legal and ethical frameworks',
        targetLevel: 4,
        category: 'generic',
    },
    {
        number: 3,
        shortLabel: 'Research',
        description: 'Know how to undertake medical research including ethical considerations, methodology and how to manage and interpret data appropriately',
        targetLevel: 4,
        category: 'generic',
    },
    {
        number: 4,
        shortLabel: 'Teaching & supervision',
        description: 'Be an effective clinical teacher and provide educational and clinical supervision',
        targetLevel: 4,
        category: 'generic',
    },
    {
        number: 5,
        shortLabel: 'Resuscitation, stabilisation & transfer',
        description: 'Identify, resuscitate and stabilise a critically ill patient, and undertake safe intra- or inter-hospital transfer to an appropriately staffed and equipped facility',
        targetLevel: 4,
        category: 'general_icm',
    },
    {
        number: 6,
        shortLabel: 'Investigation, monitoring & organ support',
        description: 'Initiate, request and interpret investigations and advanced monitoring to aid diagnosis and management of organ system failure, and provide and manage advanced organ support therapies, pharmacological and mechanical',
        targetLevel: 4,
        category: 'general_icm',
    },
    {
        number: 7,
        shortLabel: 'Perioperative care',
        description: 'Provide pre-operative resuscitation and optimisation, deliver post-operative care including physiological optimisation, advanced organ support and pain relief',
        targetLevel: 4,
        category: 'general_icm',
    },
    {
        number: 8,
        shortLabel: 'Consequences of critical illness & end of life',
        description: 'Manage the physical and psychosocial consequences of critical illness for patients and families, including pain, delirium, rehabilitation, withholding and withdrawal of life-sustaining treatment, end-of-life discussion and organ donation',
        targetLevel: 4,
        category: 'general_icm',
    },
    {
        number: 9,
        shortLabel: 'Leading a critical care service',
        description: 'Lead and manage a critical care service including the multidisciplinary team, providing contemporaneous care to a number of critically ill patients',
        targetLevel: 4,
        category: 'general_icm',
    },
    {
        number: 10,
        shortLabel: 'Anaesthesia',
        description: 'Skills of induction of anaesthesia, airway control, care of the unconscious patient and understanding of surgery and its physiological impact',
        targetLevel: 3,
        category: 'complementary',
    },
    {
        number: 11,
        shortLabel: 'Medicine',
        description: 'Diagnostic, investigational and management skills required to care for ward-based patients whose condition commonly requires ICU admission',
        targetLevel: 3,
        category: 'complementary',
    },
    {
        number: 12,
        shortLabel: 'Neuro ICM',
        description: 'Manage patients with neurological disease, medical and surgical, including raised intracranial pressure, CNS infection and neuromuscular disorders',
        targetLevel: 3,
        category: 'specialty_icm',
    },
    {
        number: 13,
        shortLabel: 'Paediatric ICM',
        description: 'Recognise, initially stabilise and manage common paediatric emergencies until expert help is available; familiarity with safeguarding legislation',
        targetLevel: 3,
        category: 'specialty_icm',
    },
    {
        number: 14,
        shortLabel: 'Cardiothoracic ICM',
        description: 'Perioperative care of patients following cardiothoracic surgery, including pain relief and advanced organ support using specialised cardiovascular techniques',
        targetLevel: 3,
        category: 'specialty_icm',
    },
];
const BY_NUMBER = new Map(HILLOS.map((hillo) => [hillo.number, hillo]));
export function hilloByNumber(number) {
    return BY_NUMBER.get(number);
}
export function isHilloNumber(value) {
    return Number.isInteger(value) && value >= 1 && value <= 14;
}
/** The paediatric HiLLO, added automatically for entries in patients under 16. */
export const PAEDIATRIC_HILLO = 13;
//# sourceMappingURL=hillos.js.map